/* PvpBattleArena — the cinematic PvP battle experience.
 *
 * Flow:
 *   1. Fetch /api/battles/{id} → server-authoritative doc with rounds[]
 *   2. Show BattleIntro (dark → logo → RIVAL BATTLE → sides → VS → arena)
 *   3. Build a typed event queue from rounds[] (client-side pacing only)
 *   4. Walk the queue with a speed-adjustable timer, animating each event
 *   5. Show BattleOutcome (Victory/Defeat) → Report → Share → Rematch
 *
 * The client NEVER decides who wins. Every animation reflects a
 * pre-computed server event.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { buildEventQueue } from '../lib/battleEventQueue';
import { getFx } from '../lib/battleFx';

import WeatherLayer from '../components/pvp/WeatherLayer';
import BattleIntro from '../components/pvp/BattleIntro';
import BattleCard from '../components/pvp/BattleCard';
import BattleControls, { loadPref, savePref } from '../components/pvp/BattleControls';
import BattleOutcome, { BattleReport, ShareResultCard } from '../components/pvp/BattleOutcome';
import { HPBar, StatusEffects, PlayerPanel, RoundTracker, MomentumMeter, RoundBanner } from '../components/pvp/BattleHUD';
import { FxLayer } from '../components/pvp/BattleFX';
import ActionPrompt from '../components/pvp/ActionPrompt';
import HabitatAffinityBadge from '../components/battle/HabitatAffinityBadge';
import EcologyIntroCard from '../components/battle/EcologyIntroCard';
import EcologySheet from '../components/battle/EcologySheet';
import { Leaf } from 'lucide-react';

const SKIP_KEY = 'wq_battle_intro_skipped_v1';

function loadOwnerPreview(users, id) {
  return users?.[id] || null;
}

// -----------------------------------------------------------------------
export default function PvpBattleArena() {
  const nav = useNavigate();
  const loc = useLocation();
  const { battleId } = useParams();
  const { user } = useAuth();
  const [battle, setBattle] = useState(loc.state?.battle || null);
  const [loadErr, setLoadErr] = useState(null);
  const [users, setUsers] = useState({});
  const motionOk = useMemo(() => {
    try { return !window.matchMedia('(prefers-reduced-motion: reduce)').matches; }
    catch { return true; }
  }, []);

  // Load the battle + both sides' explorer mini-profiles for the intro.
  useEffect(() => {
    if (battle) return;
    api.get(`/battles/${battleId}`).then(r => setBattle(r.data)).catch(() => setLoadErr('Battle not found'));
  }, [battleId, battle]);
  useEffect(() => {
    if (!battle) return;
    const ids = [battle.challenger_owner_id, battle.opponent_owner_id].filter(Boolean);
    Promise.all(ids.map(id =>
      api.get(`/explorers/${id}/mini`).then(r => [id, r.data]).catch(() => [id, null])
    )).then((pairs) => {
      const map = {}; pairs.forEach(([id, data]) => { if (data?.found) map[id] = data; });
      setUsers(map);
    });
  }, [battle]);

  // -----------------------------------------------------------------
  // Playback state
  // -----------------------------------------------------------------
  const events = useMemo(() => (battle ? buildEventQueue(battle) : []), [battle]);
  const [phase, setPhase] = useState('intro');           // 'intro' | 'playing' | 'outcome' | 'report' | 'share'
  const [speed, setSpeed] = useState(() => loadPref('wq_battle_speed', 1));
  const [soundOn, setSoundOn] = useState(() => loadPref('wq_battle_sound', true));
  const fx = useMemo(() => getFx({ soundOn, motionOk }), [soundOn, motionOk]);

  // Live view state — updated as events fire.
  const [hp, setHp] = useState({ challenger: 100, opponent: 100 });
  const [hpMax, setHpMax] = useState({ challenger: 100, opponent: 100 });
  const [status, setStatus] = useState({ challenger: [], opponent: [] });
  const [wins, setWins] = useState({ challenger: 0, opponent: 0 });
  const [currentRound, setCurrentRound] = useState(1);
  const [isFinalRound, setIsFinalRound] = useState(false);
  const [showRoundBanner, setShowRoundBanner] = useState(false);
  const [momentum, setMomentum] = useState(0);
  const [impact, setImpact] = useState({ challenger: null, opponent: null });
  const [floaters, setFloaters] = useState([]);
  const [narrative, setNarrative] = useState('');
  const [ecologyOpen, setEcologyOpen] = useState(false);
  const [introDismissed, setIntroDismissed] = useState(false);
  const [shieldActive, setShieldActive] = useState(false);

  // Tap-shield — when the battle transitions into 'outcome', swallow
  // all pointer events for 700ms so mash-happy players don't
  // accidentally hit the Rematch / Exit buttons on the newly-mounted
  // outcome screen.
  useEffect(() => {
    if (phase !== 'outcome') { setShieldActive(false); return; }
    setShieldActive(true);
    const t = setTimeout(() => setShieldActive(false), 700);
    return () => clearTimeout(t);
  }, [phase]);

  // Initialise HP once battle loads — round 1 hp_max is authoritative.
  useEffect(() => {
    if (!battle?.rounds?.[0]?.hp_max) return;
    setHpMax({
      challenger: battle.rounds[0].hp_max.challenger || 100,
      opponent: battle.rounds[0].hp_max.opponent || 100,
    });
    setHp({
      challenger: battle.rounds[0].hp_max.challenger || 100,
      opponent: battle.rounds[0].hp_max.opponent || 100,
    });
  }, [battle]);

  // -----------------------------------------------------------------
  // Fx layer helper — ephemeral floaters (damage numbers, callouts).
  // -----------------------------------------------------------------
  const addFloater = useCallback((f) => {
    const id = `fx-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    setFloaters((arr) => [...arr, { ...f, id }]);
    setTimeout(() => setFloaters((arr) => arr.filter(x => x.id !== id)), 1500);
  }, []);

  // -----------------------------------------------------------------
  // Group events into "beats" the player commands with a tap.
  // A beat = the events leading up to a decisive moment (damage
  // landed, miss, round_end, battle_end). Players tap ATTACK / READY
  // to consume a beat. Between beats we PAUSE, awaiting input, so it
  // feels like a two-player battle instead of a movie.
  // -----------------------------------------------------------------
  const beats = useMemo(() => {
    const out = [];
    let bag = [];
    events.forEach((ev) => {
      bag.push(ev);
      // "Punctuation" events end a beat.
      if (ev.type === 'damage' || ev.type === 'miss' ||
          ev.type === 'round_end' || ev.type === 'battle_end') {
        out.push(bag); bag = [];
      }
    });
    if (bag.length) out.push(bag);
    return out;
  }, [events]);

  const [beatIdx, setBeatIdx] = useState(0);
  const [awaiting, setAwaiting] = useState('start');    // 'start' | 'attack' | 'defend' | 'ready' | 'end' | null (mid-beat playback)
  const [autoPlay, setAutoPlay] = useState(() => loadPref('wq_battle_autoplay', false));

  // Determine what the pending beat is about — used to pick the prompt.
  const pendingContext = useMemo(() => {
    if (beatIdx >= beats.length) return { kind: 'end' };
    const beat = beats[beatIdx];
    // Find the first "actor" in the beat (ability or damage event).
    const actor = beat.find(e => e.type === 'ability') ||
                  beat.find(e => e.type === 'attack')  ||
                  beat.find(e => e.type === 'damage')  ||
                  beat.find(e => e.type === 'miss');
    let side = null;
    if (actor?.type === 'ability') side = actor.side;
    else if (actor?.type === 'attack') side = actor.side;
    else if (actor?.type === 'damage') side = actor.target_side === 'challenger' ? 'opponent' : 'challenger';
    else if (actor?.type === 'miss') side = actor.side;
    // Detect beat-boundary role.
    const hasRoundEnd = beat.some(e => e.type === 'round_end');
    const hasBattleEnd = beat.some(e => e.type === 'battle_end');
    const startsRound = beat[0]?.type === 'round_start';
    return { kind: hasBattleEnd ? 'battle_end' : hasRoundEnd ? 'round_end' : startsRound ? 'round_start' : 'move', side, startsRound };
  }, [beatIdx, beats]);

  // Set the correct waiting prompt each time we finish playing a beat.
  useEffect(() => {
    if (phase !== 'playing') return;
    if (beatIdx >= beats.length) { setPhase('outcome'); return; }
    if (awaiting !== null) return;   // already awaiting
    // Decide what to prompt for the NEXT beat.
    if (pendingContext.kind === 'battle_end') setAwaiting('end');
    else if (pendingContext.kind === 'round_end') setAwaiting('ready');
    else if (pendingContext.side === mySide.current) setAwaiting('attack');
    else setAwaiting('defend');
  }, [phase, beatIdx, beats.length, awaiting, pendingContext]);

  // -----------------------------------------------------------------
  // Play a single beat — chains events with short internal delays. On
  // 2× speed the internal timing is halved. Skip still pushes us
  // straight to the outcome.
  // -----------------------------------------------------------------
  const skipRef = useRef(false);
  const playBeat = useCallback(async () => {
    const beat = beats[beatIdx];
    if (!beat) return;
    setAwaiting(null);   // mid-beat, no prompt
    for (let i = 0; i < beat.length; i++) {
      if (skipRef.current) break;
      const ev = beat[i];
      const dur = Math.max(60, Math.round((ev.duration_ms || 300) / speed));
      applyEvent(ev);
      // eslint-disable-next-line no-await-in-loop
      await new Promise(res => setTimeout(res, dur));
    }
    setBeatIdx(i => i + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [beatIdx, beats, speed]);

  // Apply an individual event to state / FX layer. Extracted from the
  // old useEffect driver so both tap-play and skip can reuse it.
  function applyEvent(ev) {
    switch (ev.type) {
      case 'round_start':
        setCurrentRound(ev.round);
        setIsFinalRound(!!ev.is_final);
        setShowRoundBanner(true);
        setNarrative('');
        setTimeout(() => setShowRoundBanner(false), 900);
        if (ev.is_final) addFloater({ type: 'callout', kind: 'final-stand', text: 'FINAL STAND' });
        break;
      case 'log':
        setNarrative(ev.text);
        break;
      case 'ability':
        addFloater({ type: 'ability', side: ev.side, name: ev.name, actor: ev.actor });
        setImpact(s => ({ ...s, [ev.side]: 'attack' }));
        fx.play('ability');
        fx.hapt('light');
        setTimeout(() => setImpact(s => ({ ...s, [ev.side]: null })), 300);
        break;
      case 'attack':
        setImpact(s => ({ ...s, [ev.side]: 'attack' }));
        fx.play('hit');
        setTimeout(() => setImpact(s => ({ ...s, [ev.side]: null })), 250);
        break;
      case 'miss':
        addFloater({ type: 'miss', side: ev.side });
        fx.play('miss');
        break;
      case 'damage':
        setImpact(s => ({ ...s, [ev.target_side]: 'hit' }));
        setHp(prev => ({
          ...prev,
          [ev.target_side]: Math.max(0, ev.hp_after_target != null ? ev.hp_after_target : prev[ev.target_side] - ev.value),
        }));
        if (ev.hp_max) setHpMax(prev => ({ ...prev, [ev.target_side]: ev.hp_max }));
        addFloater({ type: 'damage', side: ev.target_side, value: ev.value, crit: ev.crit });
        fx.play(ev.crit ? 'crit' : 'hit');
        fx.hapt(ev.crit ? 'medium' : 'light');
        setTimeout(() => setImpact(s => ({ ...s, [ev.target_side]: null })), 340);
        break;
      case 'status_applied':
        setStatus(prev => {
          const list = prev[ev.target_side] || [];
          if (list.some(x => x.code === ev.code)) return prev;
          return { ...prev, [ev.target_side]: [...list, { code: ev.code, name: ev.name }] };
        });
        break;
      case 'critical_callout':
        addFloater({ type: 'callout', kind: ev.kind, text: ev.text });
        if (ev.kind === 'critical') fx.play('crit');
        break;
      case 'round_end':
        setWins(ev.round_wins || wins);
        setStatus({ challenger: [], opponent: [] });
        setMomentum(m => Math.max(-1, Math.min(1, m + (ev.winner_side === 'challenger' ? 0.35 : -0.35))));
        fx.play(ev.winner_side === mySide.current ? 'round_win' : 'round_loss');
        break;
      case 'battle_end':
        fx.play(ev.winner_side === mySide.current ? 'victory' : 'defeat');
        fx.hapt(ev.winner_side === mySide.current ? 'celebration' : 'medium');
        setImpact(s => ({ ...s, [ev.winner_side === 'challenger' ? 'opponent' : 'challenger']: 'defeated' }));
        break;
      default:
        break;
    }
  }

  // Autoplay — when the toggle is on, defender/round/end prompts advance
  // themselves after a short delay so the player can put the phone
  // down. Attack prompts still require a tap so the moment of agency
  // is preserved.
  useEffect(() => {
    if (!autoPlay) return;
    if (phase !== 'playing') return;
    if (awaiting === 'defend' || awaiting === 'ready' || awaiting === 'end') {
      const t = setTimeout(() => playBeat(), 800);
      return () => clearTimeout(t);
    }
  }, [autoPlay, awaiting, phase, playBeat]);

  // -----------------------------------------------------------------
  // My side — derived from `user.user_id` vs battle owners.
  // -----------------------------------------------------------------
  const mySide = useRef('challenger');
  useEffect(() => {
    if (!battle || !user) return;
    if (battle.opponent_owner_id === user.user_id) mySide.current = 'opponent';
    else mySide.current = 'challenger';
  }, [battle, user]);

  const skipToOutcome = () => {
    skipRef.current = true;
    setAwaiting(null);
    // Snap all UI state to the final battle values so the outcome screen
    // opens with the correct numbers even if playback was mid-way.
    const last = battle?.rounds?.[battle.rounds.length - 1];
    if (last?.hp_after) setHp({ ...last.hp_after });
    if (last?.hp_max) setHpMax({ ...last.hp_max });
    setWins(battle?.round_wins || wins);
    setStatus({ challenger: [], opponent: [] });
    setPhase('outcome');
  };

  // Rematch — creates a NEW duel with the same pair + same cards.
  const rematch = async () => {
    try {
      const oppUserId = mySide.current === 'challenger' ? battle.opponent_owner_id : battle.challenger_owner_id;
      const myCardId = mySide.current === 'challenger' ? battle.challenger_card_id : battle.opponent_card_id;
      const { data } = await api.post('/duels/challenge', {
        opponent_user_id: oppUserId,
        challenger_card_id: myCardId,
        message: 'Rematch — same cards.',
      });
      toast.success('Rematch sent!', {
        description: 'They have 48 hours to accept.',
        action: { label: 'View', onClick: () => nav('/battle/duels') },
      });
      nav('/battle/duels');
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 409) toast.error('You already have a pending challenge with this Explorer.', { action: { label: 'View', onClick: () => nav('/battle/duels') } });
      else toast.error(typeof detail === 'string' ? detail : 'Could not send rematch');
    }
  };

  if (loadErr) {
    return <div className="fixed inset-0 flex items-center justify-center bg-black text-white/60 text-sm">{loadErr}</div>;
  }
  if (!battle) {
    return <div className="fixed inset-0 flex items-center justify-center bg-black text-white/40 text-xs uppercase tracking-widest">Loading battle…</div>;
  }

  const challengerOwner = loadOwnerPreview(users, battle.challenger_owner_id);
  const opponentOwner = loadOwnerPreview(users, battle.opponent_owner_id);
  const myNickname = mySide.current === 'challenger' ? (challengerOwner?.nickname || 'You') : (opponentOwner?.nickname || 'You');
  const oppNickname = mySide.current === 'challenger' ? (opponentOwner?.nickname || 'Rival') : (challengerOwner?.nickname || 'Rival');

  return (
    <div className="fixed inset-0 bg-black text-white overflow-hidden select-none" data-testid="pvp-battle-arena" onClick={() => fx.unlock()}>
      {/* Ambient arena + weather */}
      <WeatherLayer arena={battle.arena} weather={battle.weather} motionOk={motionOk} />

      {/* Controls */}
      {phase !== 'intro' && (
        <BattleControls
          speed={speed} onSpeed={setSpeed}
          soundOn={soundOn} onSound={setSoundOn}
          autoPlay={autoPlay}
          onAutoPlay={(v) => { setAutoPlay(v); savePref('wq_battle_autoplay', v); }}
          onSkip={skipToOutcome}
          onExit={() => nav(-1)}
          disabled={phase === 'outcome'}
        />
      )}

      {/* Cinematic intro */}
      <AnimatePresence>
        {phase === 'intro' && (
          <BattleIntro
            battle={battle}
            challenger={{
              name: challengerOwner?.nickname || 'Challenger',
              rank: challengerOwner?.rank || 'Explorer',
              tier: challengerOwner?.tier,
              avatar: challengerOwner?.picture,
              cardPhoto: battle.challenger_snapshot?.picture,
              cardName: battle.challenger_snapshot?.name,
              cardRarity: battle.challenger_snapshot?.rarity,
            }}
            opponent={{
              name: opponentOwner?.nickname || 'Opponent',
              rank: opponentOwner?.rank || 'Explorer',
              tier: opponentOwner?.tier,
              avatar: opponentOwner?.picture,
              cardPhoto: battle.opponent_snapshot?.picture,
              cardName: battle.opponent_snapshot?.name,
              cardRarity: battle.opponent_snapshot?.rarity,
            }}
            onDone={() => setPhase('playing')}
            onSkip={() => setPhase('playing')}
          />
        )}
      </AnimatePresence>

      {/* Arena main layout — only shown once intro finished */}
      {phase !== 'intro' && (
        <>
          {/* Top HUD (opponent) */}
          <div className="wq-pvp-hud-top absolute top-3 left-3 right-24 z-20 max-w-md">
            <PlayerPanel
              side="opponent"
              name={oppNickname}
              avatar={opponentOwner?.picture}
              snapshot={battle.opponent_snapshot}
              hp={hp.opponent}
              hpMax={hpMax.opponent}
              status={status.opponent}
              isYou={mySide.current === 'opponent'}
            />
          </div>

          {/* Center stage — battle cards */}
          <div className="absolute inset-0 flex items-center justify-center gap-4 sm:gap-8 px-3">
            <BattleCard
              side="challenger"
              snapshot={battle.challenger_snapshot}
              serial={battle.challenger_snapshot?.serial_label?.split('·')?.[0]?.trim()}
              isActive={true}
              impactState={impact.challenger}
            />
            <BattleCard
              side="opponent"
              snapshot={battle.opponent_snapshot}
              serial={battle.opponent_snapshot?.serial_label?.split('·')?.[0]?.trim()}
              isActive={true}
              impactState={impact.opponent}
            />
          </div>

          {/* Center HUD — round tracker + momentum. Uses solid tinted
              bg (was backdrop-blur which spiked mobile GPU cost). */}
          <div className="wq-pvp-round-hud absolute top-24 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 z-30 pointer-events-none bg-black/60 rounded-2xl px-4 py-2 border border-white/10">
            <RoundTracker bestOf={battle.best_of || 5} wins={wins} isFinal={isFinalRound} />
            <MomentumMeter value={momentum} />
            {battle?.battle_prep?.habitat_affinity && (battle.battle_prep.habitat_affinity.challenger_pct > 0 || battle.battle_prep.habitat_affinity.opponent_pct > 0) && (
              <div className="flex items-center gap-1.5 mt-0.5" data-testid="pvp-habitat-affinity-row">
                <HabitatAffinityBadge
                  pct={battle.battle_prep.habitat_affinity.challenger_pct}
                  arena={battle.battle_prep.habitat_affinity.arena_label || battle.arena}
                  compact
                  testId="pvp-habitat-affinity-ch"
                />
                {battle.battle_prep.habitat_affinity.is_shared_habitat && (
                  <span className="text-[8px] uppercase tracking-widest text-emerald-200/80" data-testid="pvp-habitat-shared">shared</span>
                )}
                <HabitatAffinityBadge
                  pct={battle.battle_prep.habitat_affinity.opponent_pct}
                  arena={battle.battle_prep.habitat_affinity.arena_label || battle.arena}
                  compact
                  testId="pvp-habitat-affinity-op"
                />
              </div>
            )}
            {/* Ecology deep-dive button — pointer-events-auto because the
               parent HUD block is pointer-events-none for the tracker. */}
            <button
              type="button"
              onClick={() => setEcologyOpen(true)}
              data-testid="pvp-ecology-btn"
              aria-label="Learn ecology of both species in this battle"
              className="pointer-events-auto inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-400/40 hover:bg-emerald-500/20 text-[9px] uppercase tracking-widest text-emerald-200 mt-0.5"
            >
              <Leaf className="w-3 h-3" strokeWidth={2.4} /> Ecology
            </button>
          </div>

          {/* Narrative line */}
          <AnimatePresence mode="wait">
            {narrative && (
              <motion.p
                key={narrative}
                initial={{ opacity: 0, y: 6 }} animate={{ opacity: 0.9, y: 0 }} exit={{ opacity: 0 }}
                className="absolute inset-x-0 bottom-40 text-center text-xs text-white/80 px-8"
                data-testid="battle-narrative"
              >
                {narrative}
              </motion.p>
            )}
          </AnimatePresence>

          {/* FX overlays — damage numbers, callouts, ability flashes */}
          <FxLayer effects={floaters} />

          {/* Round banner */}
          {showRoundBanner && <RoundBanner round={currentRound} isFinal={isFinalRound} />}

          {/* Bottom HUD (challenger / you) */}
          <div className="wq-pvp-hud-bottom absolute bottom-3 left-3 right-3 z-20">
            <PlayerPanel
              side="challenger"
              name={myNickname}
              avatar={challengerOwner?.picture}
              snapshot={battle.challenger_snapshot}
              hp={hp.challenger}
              hpMax={hpMax.challenger}
              status={status.challenger}
              isYou={mySide.current === 'challenger'}
            />
          </div>

          {/* Interactive tap-driven action prompt.
             Attack   — I'm the actor of the next beat. Requires my tap.
             Defend   — Opponent is the actor. Tap to acknowledge (or
                         autoplay after 800ms if the toggle is on).
             Ready    — Between rounds.
             End      — Final beat before outcome screen.
             Start    — Very first beat of the battle. */}
          {awaiting && (
            <ActionPrompt
              kind={awaiting}
              mySide={mySide.current}
              roundLabel={
                awaiting === 'ready' ? `Round ${currentRound} complete`
                : awaiting === 'end' ? 'Battle over'
                : awaiting === 'attack' ? `Round ${currentRound} · your move`
                : awaiting === 'defend' ? `Round ${currentRound} · their move`
                : awaiting === 'start' ? 'Battle begins'
                : null
              }
              onTap={playBeat}
            />
          )}
        </>
      )}

      {/* Post-battle Tap-Shield — swallows any lingering taps for 700ms
         after the outcome mounts so late taps don't fire Rematch/Exit. */}
      {shieldActive && (
        <div
          data-testid="pvp-post-battle-tap-shield"
          aria-hidden
          className="fixed inset-0 z-[95]"
          style={{ background: 'transparent', touchAction: 'none' }}
          onPointerDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
          onPointerUp={(e) => { e.preventDefault(); e.stopPropagation(); }}
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
        />
      )}

      {/* Outcome */}
      <AnimatePresence>
        {phase === 'outcome' && !shieldActive && (
          <BattleOutcome
            battle={battle}
            mySide={mySide.current}
            iWon={battle.winner === mySide.current}
            onRematch={rematch}
            onExit={() => nav('/battle/duels')}
            onOpenReport={() => setPhase('report')}
            onOpenShare={() => setPhase('share')}
          />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {phase === 'report' && (
          <BattleReport battle={battle} mySide={mySide.current} onClose={() => setPhase('outcome')} />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {phase === 'share' && (
          <ShareResultCard battle={battle} mySide={mySide.current}
            myNickname={myNickname} oppNickname={oppNickname}
            onClose={() => setPhase('outcome')} />
        )}
      </AnimatePresence>

      {/* Ecology intro subtitle — 2s fade-in when the fight starts. */}
      {!introDismissed && phase === 'playing' && battle?.arena && (
        <EcologyIntroCard
          arenaKey={battle.arena}
          arenaLabel={battle?.battle_prep?.habitat_affinity?.arena_label}
          isSharedHabitat={!!battle?.battle_prep?.habitat_affinity?.is_shared_habitat}
          onDismiss={() => setIntroDismissed(true)}
        />
      )}

      {/* Optional ecology deep-dive sheet */}
      <EcologySheet
        open={ecologyOpen}
        onClose={() => setEcologyOpen(false)}
        chSnapshot={battle?.challenger_snapshot}
        opSnapshot={battle?.opponent_snapshot}
        arenaKey={battle?.arena}
        arenaLabel={battle?.battle_prep?.habitat_affinity?.arena_label}
        isSharedHabitat={!!battle?.battle_prep?.habitat_affinity?.is_shared_habitat}
      />
    </div>
  );
}
